import { notificationTargetPath, type NotificationItem } from '@utazzo/shared';

import { navigateInApp } from '@/lib/app-navigation';
import { isStandaloneApp } from '@/lib/device';

/**
 * Where a notification leads, read off its tags. Null when it leads nowhere.
 * Lives in @utazzo/shared so the API's phone push opens the same page.
 */
export { notificationTargetPath };

/**
 * Open an in-app admin route in a new browser tab.
 *
 * Inside the installed app (PWA) there are no tabs: `window.open` would hand
 * the page to the system browser, which on iPhone has its own storage and so
 * lands on the login screen. There we navigate in place instead.
 */
export function openAdminRouteInNewTab(path: string): void {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  if (isStandaloneApp() && navigateInApp(normalized)) return;
  window.open(normalized, '_blank', 'noopener,noreferrer');
}

/** Navigate to the primary entity linked from a notification tag. */
export function openNotificationTarget(tags: NotificationItem['tags'] | undefined): void {
  const path = notificationTargetPath(tags);
  if (path) openAdminRouteInNewTab(path);
}

/**
 * Whether a notification asks for a decision (`action`) or is just news
 * (`info`). Set by the server on booking notifications; everything else that
 * links somewhere is treated as action, which is what it was before.
 */
export function notificationKind(tags: NotificationItem['tags'] | undefined): 'action' | 'info' {
  return tags?.kind === 'info' ? 'info' : 'action';
}

/** The verb on the popup / row button, per target. */
export function notificationOpenLabel(tags: NotificationItem['tags'] | undefined): string {
  if (tags?.bookingId) return tags.moment === 'cancelled' ? 'See cancellation' : 'Open booking';
  if (tags?.leadId) return 'Open lead';
  if (tags?.customerId) return 'Open customer';
  return 'Open';
}
